import { Fragment, ReactNode, useCallback, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Button } from '@/atoms/Button'
import { CircleQuestionIcon } from '@/atoms/Icons/CircleQuestionIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { LogoutIcon } from '@/atoms/Icons/LogoutIcon'
import { MenuIcon } from '@/atoms/Icons/MenuIcon'
import { PersonIcon } from '@/atoms/Icons/PersonIcon'
import { PlusIcon } from '@/atoms/Icons/PlusIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { ProfileUser } from '@/layouts/GuildProjectLayout/components/ProfileUser'
import { Avatar } from '@/molecules/Avatar'
import { useCreateNewProjectMutation } from '@/services/GuildProjectService'
import { useUser } from '@/services/UserService'
import { useLogoutUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { ProjectComboSelector } from '@/views/GuildCreateProjectsView/dashboard/ProjectComboSelector'
import { useFilmmakerDashboard } from '@/views/GuildCreateProjectsView/dashboard/context/FilmmakerDashboardContext'

export function FilmmakerMobileMenu() {
  const router = useSafeRouter()
  const { isLoggedIn, user } = useUser()
  const { logoutUrl, trackLogoutStarted } = useLogoutUrl()
  const { admin } = useFilmmakerDashboard()
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslate('guild-create')
  const createNewProject = useCreateNewProjectMutation()
  const handleCreateProject = useCallback(async () => {
    const data = await createNewProject()
    if (data.data?.createProject) {
      setIsOpen(false)
      router.push(`/guild/create/projects/${data.data?.createProject?.id}/type`)
    }
  }, [createNewProject, router, setIsOpen])

  if (!isLoggedIn) {
    // login button will show in this FilmmakerMenu component
    // Need to come back to this to centralize where the login button is rendered
    // including this here for now so it doesn't show bad state when logged out
    return null
  }

  return (
    <div className="lg:hidden">
      <button onClick={() => setIsOpen(true)} className="rounded-md p-3 hover:bg-gray-200">
        <span className="sr-only">Open sidebar</span>
        <MenuIcon color="gray-600" size={20} />
      </button>

      <Drawer show={isOpen} onClose={() => setIsOpen(false)}>
        <div className="flex h-16 shrink-0 items-center justify-between pl-6 pr-4">
          <AngelLogo className="min-w-[50px]" color="black" />
          <button onClick={() => setIsOpen(false)} className="rounded-md p-2 hover:bg-gray-200">
            <span className="sr-only">Close sidebar</span>
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <hr className="h-px border-gray-300" />

          <div className="px-6">
            <ProjectComboSelector />
          </div>

          <div className="flex items-center gap-2 px-6">
            <div className="shrink-0">
              <Avatar size={32} />
            </div>
            <ProfileUser
              firstName={user?.profile?.firstName ?? ''}
              lastName={user?.profile?.lastName ?? ''}
              email={user?.email ?? ''}
            />
          </div>

          <div className="px-4">
            <InternalLink
              href={paths.account.index}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-200"
            >
              <PersonIcon size={24} color={'gray-500'} />
              <ParagraphMD>Profile</ParagraphMD>
            </InternalLink>

            {admin && (
              <InternalLink
                href={paths.guild.projects.admin}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-200"
              >
                <PersonIcon size={24} color={'gray-500'} />
                <ParagraphMD>Employee</ParagraphMD>
              </InternalLink>
            )}
            <InternalLink
              href={logoutUrl}
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-200"
              onClick={() => {
                trackLogoutStarted('filmmaker-nav')
                setIsOpen(false)
              }}
            >
              <LogoutIcon size={24} color={'gray-500'} />
              <ParagraphMD>Logout</ParagraphMD>
            </InternalLink>
          </div>

          <div className="px-6">
            <Button variant="black" size="xs" onClick={handleCreateProject}>
              <div className="flex items-center gap-1">
                <PlusIcon color="gray-500" size={18} />
                <ParagraphSM>{t('newProject', 'New Project')}</ParagraphSM>
              </div>
            </Button>
          </div>

          <div className="flex items-center gap-2 px-6">
            <CircleQuestionIcon size={24} color={'gray-500'} />
            <ParagraphSM>
              {t('needHelpQuestion', 'Need help?')}{' '}
              <a className="underline" href="mailto:tanner.nash@angel.com">
                {t('contact', 'Contact')} Tanner
              </a>
            </ParagraphSM>
          </div>
        </div>
      </Drawer>
    </div>
  )
}

function Drawer(props: { show: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Transition show={props.show} as={Fragment}>
      <Dialog onClose={props.onClose} className="relative z-50">
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <Transition.Child
          enter="transition-all duration-700 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-all duration-500 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div className="fixed inset-0 bg-black/30 lg:hidden" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          enter="transition-all duration-700 ease-out"
          enterFrom="transform translate-x-full"
          enterTo="transform translate-x-0"
          leave="transition-all duration-500 ease-in"
          leaveFrom="transform translate-x-0"
          leaveTo="transform translate-x-full"
          as={Fragment}
        >
          {/* Full-screen scrollable container */}
          <div className="fixed inset-0 flex w-screen justify-end overflow-y-auto lg:hidden">
            <Dialog.Panel className="flex w-full flex-col bg-gray-100 py-4 sm:w-80">{props.children}</Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  )
}
